import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
    REVSTACK_CHART_COLORS,
    REVSTACK_TRENDS_CHART_COLORS,
} from "@ts/constants";
import { capitalizeWords, titleCase } from "@ts/utils";
import { Button, Skeleton, Tooltip } from "antd";
import NoDataText from "./NoDataText";
import {
    Channels,
    CommonWidgetMetrics,
    RevstackFilterTypes,
} from "@ts/common-types";
import { useResponsiveChartHeight } from "@hooks/AllCustomHooks";
import { createLineChartGradientFillColor, sortChartDataByFiltersDataOrder } from "@/ts/revstack/helpers";
import { buildWidgetCardFilters } from "@/ts/revstack/ui-utils";
import { createSyncEnabledChartOptions, useChartSync } from "@/ts/revstack/chart-sync-utils";

interface IRevstackTrendsWidgetProps {
    widgetData: any;
    yAxisUnit?: string;
    xAxisUnit?: string;
    chartComponentRef: React.RefObject<HighchartsReact.RefObject>;
    isLoading?: boolean;
    handleSetYAxisFilter?: (value: Record<string, any>[]) => void;
    yAxisFilter?: string;
    yAxisOppositeFilter?: string;
    identifier?: string;
    widget?: any;
    hasChartPillsFilters: boolean;
    selectedPills?: Array<{ name: string; value: unknown; color: any; }>;
    disableYaxisOppositeOnPillCount?: number;
    pillFilterDropDownData?: any[];
    // Tooltip synchronization props
    chartSyncId?: string;
    enableTooltipSync?: boolean;
}

const COMMON_STYLES = {
    fontFamily: "Manrope, sans-serif",
    secondaryColor: "#919191",
    primaryColor: "#0078AA",
    textColor: "#18181B",
    whiteColor: "#fff",
} as const;

const LEGEND_STYLES = {
    legendItem: {
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: "50%",
        marginRight: 6,
        fontSize: 11,
    },
    legendText: {
        fontWeight: 400,
        color: COMMON_STYLES.textColor,
        fontSize: 11,
    },
    tooltipButton: {
        border: `1.5px solid ${COMMON_STYLES.primaryColor}`,
        borderRadius: 6,
        color: COMMON_STYLES.primaryColor,
        fontWeight: 600,
        fontSize: 11,
        width: 28.571,
        height: 25,
        lineHeight: "18px",
        background: COMMON_STYLES.whiteColor,
        padding: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
} as const;

if ((Highcharts.Series.prototype as any).drawLegendSymbol) {
    (Highcharts.Series.prototype as any).drawLegendSymbol = function () {
        // Custom legend symbol drawing disabled
    };
}

// Memoized CustomLegend component
const CustomLegend = React.memo(
    ({ series, graphColors, formatForChannelId }: { series: any[]; graphColors: string[]; formatForChannelId: boolean }) => {
        const MAX_VISIBLE = 4;
        const MAX_TEXT_LENGTH = 12;
        const visibleSeries = series.slice(0, MAX_VISIBLE);
        const hiddenSeries = series.slice(MAX_VISIBLE);

        const truncateText = (text: string) => {
            const capitalizedText = capitalizeWords(text);
            return capitalizedText.length > MAX_TEXT_LENGTH
                ? `${capitalizedText.substring(0, MAX_TEXT_LENGTH)}...`
                : capitalizedText;
        };

        const shouldShowTooltip = (text: string) => {
            return capitalizeWords(text).length > MAX_TEXT_LENGTH;
        };

        return (
            <div
                className="d-flex justify-content-center align-items-center gap-3 mb-1"
                style={{ fontSize: 12 }}
            >
                {visibleSeries.map((s: any, idx: number) => {
                    const fullText = capitalizeWords(s.name);
                    const displayText = truncateText(s.name);
                    const needsTooltip = shouldShowTooltip(s.name);

                    const legendItem = (
                        <div
                            className="d-flex align-items-center gap-2"
                            key={`${s.name}-${idx}`}
                            style={{
                                fontSize: 12,
                                cursor: needsTooltip ? 'pointer' : 'default'
                            }}
                        >
                            <span
                                style={{
                                    ...LEGEND_STYLES.legendItem,
                                    background: s.color || (graphColors && graphColors[idx]) || COMMON_STYLES.primaryColor,
                                }}
                            />
                            <span style={LEGEND_STYLES.legendText}>{displayText}</span>
                        </div>
                    );

                    return needsTooltip ? (
                        <Tooltip key={`${s.name}-${idx}`} title={fullText} placement="top">
                            {legendItem}
                        </Tooltip>
                    ) : (
                        legendItem
                    );
                })}
                {hiddenSeries.length > 0 && (
                    <Tooltip
                        title={
                            <div>
                                {hiddenSeries.map((s: any, idx: number) => (
                                    <div
                                        key={s.name}
                                        className="d-flex align-items-center gap-2"
                                        style={{ fontSize: 12 }}
                                    >
                                        <span
                                            style={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: "50%",
                                                background: s.color || (graphColors && graphColors[idx + MAX_VISIBLE]) || COMMON_STYLES.primaryColor,
                                                display: "inline-block",
                                                marginRight: 8,
                                                fontSize: 12,
                                            }}
                                        />
                                        <span
                                            style={{
                                                color: COMMON_STYLES.whiteColor,
                                                fontWeight: 500,
                                                fontSize: 12,
                                            }}
                                        >
                                            {capitalizeWords(s.name)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        }
                        placement="top"
                    >
                        <Button style={LEGEND_STYLES.tooltipButton}>
                            +{hiddenSeries.length}
                        </Button>
                    </Tooltip>
                )}
            </div>
        );
    }
);

const RevstackTrendsWidget =
    React.memo<IRevstackTrendsWidgetProps>(
        ({
            widgetData,
            chartComponentRef,
            isLoading = false,
            handleSetYAxisFilter,
            yAxisFilter,
            yAxisOppositeFilter,
            identifier,
            widget,
            hasChartPillsFilters,
            selectedPills,
            disableYaxisOppositeOnPillCount = null,
            pillFilterDropDownData = [],
            chartSyncId,
            enableTooltipSync = false
        }) => {


            const config = widget?.config;
            const yAxisTickAmount = config?.yAxisTickAmount || 5;
            const chartType = config?.chartType || "spline";
            const isCustomLegendRequired = config?.isCustomLegendRequired ?? true;
            const isChartActionsRequired = config?.isChartActionsRequired ?? false;
            const isAdvanceTrendsBtnRequired = config?.isAdvanceTrendsBtnRequired ?? false;
            const enableAdvanceTrendsBtn = config?.enableAdvanceTrendsBtn ?? false;
            const graphColors = config?.graphColors || REVSTACK_TRENDS_CHART_COLORS;
            const isChartLoadingRequired = config?.isChartLoadingRequired ?? false;
            const sortByFiltersData = config?.sortByFiltersData || false;
            const graphAttributes = config?.graphAttributes || {};
            const primaryDimension = widget?.queryMetaInfo?.primaryDimension || CommonWidgetMetrics.CATEGORY;
            const filterOptionsOn = config?.filterOptionsOn || "category";
            const allFilterOptionKey = config?.allFilterOptionKey || "Categories";
            const isWidgetFilterRequired = config.isWidgetFilterRequired || false;
            const widgetFilterConfig = config?.widgetFilterConfig || {};
            const minorTicksLabelLimit = config?.graphAttributes?.minorTicksLabelLimit || 14;
            const isNestedChart = config?.isNestedChart || false;
            const formatForChannelId = config?.formatForChannelId || false;
            const minDatelabels = config?.graphAttributes?.minDatelabels || 14;
            const maxYaxisValue = config?.graphAttributes?.maxYaxisValue || null;

            const [widgetFilterOptions, setWidgetFilterOptions] = useState<any[]>([]);
            const [widgetFilter, setWidgetFilter] = useState<string>(widgetFilterConfig.defaultValue || "");

            // Chart synchronization setup
            const { registerChart, unregisterChart } = useChartSync(chartSyncId || identifier || '', chartComponentRef);


            const originalSeries = useMemo(() => {
                return JSON.parse(JSON.stringify(widgetData?.chartDataSeries?.series || []));
            }, [JSON.stringify(widgetData?.chartDataSeries?.series)]);
            const categories = widgetData?.chartDataSeries?.categories || [];


            useEffect(() => {

                if (handleSetYAxisFilter) {
                    let categories = [
                        ...new Set(
                            (widgetData?.chartDataSeries?.series || [])
                                .map((item: any) => item[filterOptionsOn])
                                .filter(Boolean)
                        ),
                    ];

                    // Sort so "all" (case-insensitive) is at the top
                    categories = categories.sort((a, b) => {
                        if (String(a).toLowerCase() === CommonWidgetMetrics.All) return -1;
                        if (String(b).toLowerCase() === CommonWidgetMetrics.All) return 1;
                        return 0;
                    });

                    const filterOptions = categories.map((category) => ({
                        label: String(category).toLowerCase() === CommonWidgetMetrics.All ? `All ${allFilterOptionKey}` : capitalizeWords(String(category)),
                        value: category,
                    }));

                    handleSetYAxisFilter && handleSetYAxisFilter(filterOptions);
                }

                if (isWidgetFilterRequired) {
                    const filterChartDataOn = widgetFilterConfig.filterChartDataOn || "name";
                    let widgetFilterOptions = [
                        ...new Set(
                            (widgetData?.chartDataSeries?.series || [])
                                .map((item: any) => item[filterChartDataOn])
                                .filter(Boolean)
                        ),
                    ];

                    const filterOptions = widgetFilterOptions.map((option) => ({
                        label: String(option),
                        value: option,
                    }));

                    setWidgetFilterOptions(filterOptions);
                }

            }, [widgetData?.chartDataSeries?.series?.length]);

            // Register/unregister chart for synchronization
            useEffect(() => {
                if (enableTooltipSync && chartComponentRef?.current?.chart) {
                    registerChart();
                }

                return () => {
                    if (enableTooltipSync) {
                        unregisterChart();
                    }
                };
            }, [enableTooltipSync, chartComponentRef?.current?.chart, registerChart, unregisterChart]);


            const series = useMemo(() => {
                // Don't render any data until pills are initialized (prevents flash)
                if (hasChartPillsFilters && (!selectedPills || selectedPills.length === 0)) {
                    return [];
                }

                // Create deep copies to prevent reference mutation
                let filteredSeries = originalSeries.map((item: any) => ({
                    ...item,
                    data: [...(item.data || [])] // Deep copy the data array
                }));

                // Sort series using the common helper function
                filteredSeries = sortChartDataByFiltersDataOrder(filteredSeries, pillFilterDropDownData, primaryDimension, sortByFiltersData);
                const filterYaxisAttribute = config?.filterYaxisAttribute || CommonWidgetMetrics.CATEGORY;
                if (yAxisFilter) {
                    filteredSeries = filteredSeries.filter((item: any) => {
                        const matchYAxis = item[filterYaxisAttribute] === yAxisFilter;
                        // if (yAxisOppositeFilter) {
                        //     const result = matchYAxis && item[filterYaxisAttribute] !== CommonWidgetMetrics.All;
                        //     return result;
                        // } 
                        if (item[filterYaxisAttribute] == yAxisFilter) {
                            return matchYAxis;
                        }
                    });

                }
                if (isWidgetFilterRequired) {
                    const filterChartDataOn = widgetFilterConfig.filterChartDataOn || "name";
                    filteredSeries = filteredSeries.filter((item: any) => {
                        return item[filterChartDataOn] === widgetFilter;
                    });
                }

                // Apply selected pills filtering if pills are selected
                if (selectedPills && selectedPills.length > 0) {
                    const selectedPillValues = selectedPills.map((pill: any) => pill.value);

                    filteredSeries = filteredSeries.filter((item: any) => {
                        // Determine which property to use for filtering based on what exists in the data
                        const filterProperty = Object.prototype.hasOwnProperty.call(item, primaryDimension) ? primaryDimension : 'subcategory'

                        return selectedPillValues.includes(item[filterProperty]);
                    });
                }

                // Update series colors to match selected pills colors
                filteredSeries = filteredSeries.map((item: any, index: number) => {
                    let seriesColor;

                    if (hasChartPillsFilters && selectedPills && selectedPills.length > 0) {
                        // single pill always uses primary color
                        if (selectedPills.length === 1) {
                            seriesColor = REVSTACK_CHART_COLORS.primary[700];
                        } else {
                            const filterProperty = Object.prototype.hasOwnProperty.call(item, primaryDimension) ? primaryDimension : 'subcategory';
                            const itemValue = item[filterProperty];

                            // Find the index of this item in the selected pills to get consistent color
                            const pillIndex = selectedPills.findIndex((pill: any) => pill.value === itemValue);

                            if (pillIndex !== -1) {
                                // Use the pill's color directly
                                seriesColor = selectedPills[pillIndex].color;
                            } else {
                                seriesColor = graphColors[index];
                            }
                        }
                    } else {
                        seriesColor = graphColors[index];
                    }

                    // Format series name for display (used in tooltip and legend)
                    const displayName = formatForChannelId
                        ? titleCase((Channels as any)[item.name] || item.name)
                        : item.name;

                    return {
                        ...item,
                        name: displayName,
                        color: seriesColor,
                        lineWidth: 1.5,
                        fillColor: createLineChartGradientFillColor(seriesColor),
                    };
                });

                return filteredSeries;
            }, [originalSeries, yAxisFilter, selectedPills, hasChartPillsFilters,
                primaryDimension, sortByFiltersData, pillFilterDropDownData, graphColors, formatForChannelId, widgetFilter]);

            // Opposite Y-axis series (only when single pill is selected)
            const oppositeYAxisSeries = useMemo(() => {
                if ((disableYaxisOppositeOnPillCount !== null && selectedPills?.length !== disableYaxisOppositeOnPillCount) || !yAxisOppositeFilter) {
                    return [];
                }

                // Don't render opposite axis data until pills are initialized
                if (hasChartPillsFilters && (!selectedPills || selectedPills.length === 0)) {
                    return [];
                }
                // Create deep copies to prevent reference mutation
                let oppositeSeries = originalSeries.map((item: any) => ({
                    ...item,
                    data: [...(item.data || [])] // Deep copy the data array
                }));
                const filterYaxisOppositeAttribute = config?.filterYaxisOppositeAttribute || CommonWidgetMetrics.CATEGORY;

                if (yAxisOppositeFilter && !(disableYaxisOppositeOnPillCount !== null && selectedPills?.length !== disableYaxisOppositeOnPillCount)) {
                    oppositeSeries = oppositeSeries.filter((item: any) => {
                        return item[filterYaxisOppositeAttribute] == yAxisOppositeFilter
                    });

                }
                // Sort opposite series using the common helper function
                oppositeSeries = sortChartDataByFiltersDataOrder(oppositeSeries, pillFilterDropDownData, primaryDimension, sortByFiltersData);


                // Filter by the selected pill
                if (selectedPills && selectedPills.length > 0) {
                    const selectedPillValue = selectedPills[0].value;
                    oppositeSeries = oppositeSeries.filter((item: any) => {
                        // Determine which property to use for filtering based on what exists in the data
                        const filterProperty =
                            Object.prototype.hasOwnProperty.call(
                                item,
                                primaryDimension
                            )
                                ? primaryDimension
                                : "subcategory";
                        return item[filterProperty] === selectedPillValue;
                    });
                }

                // Configure for opposite y-axis
                return oppositeSeries.map((item: any, index: number) => {
                    const seriesColor = REVSTACK_CHART_COLORS.secondary[500];

                    // Format series name for display (used in tooltip and legend)
                    const displayName = formatForChannelId
                        ? titleCase((Channels as any)[item.name] || item.name)
                        : item.name;

                    return {
                        ...item,
                        yAxis: 1, // Use opposite y-axis
                        color: seriesColor,
                        lineWidth: 1,
                        name: displayName,
                        fillColor: createLineChartGradientFillColor(seriesColor),
                    };
                });
            }, [originalSeries, yAxisFilter, yAxisOppositeFilter, selectedPills, primaryDimension, sortByFiltersData, pillFilterDropDownData, hasChartPillsFilters, formatForChannelId]);

            // Combined series for chart
            const combinedSeries = useMemo(() => {
                const combined = [...series, ...oppositeYAxisSeries];
                return combined;
            }, [series, oppositeYAxisSeries, selectedPills]);

            const chartHeight = useResponsiveChartHeight({ config });

            const yAxisTitles = useMemo(() => {
                let yAxisFilterTitle = yAxisFilter;
                if (handleSetYAxisFilter) {
                    yAxisFilterTitle =
                        String(yAxisFilter).toLowerCase() ===
                            CommonWidgetMetrics.All
                            ? `All ${allFilterOptionKey}`
                            : capitalizeWords(yAxisFilter);
                }

                return {
                    yAxisTitle:
                        yAxisFilterTitle ||
                        widgetFilter ||
                        graphAttributes.yAxisTitle ||
                        "",
                    oppositeYAxisTitle:
                        yAxisOppositeFilter ||
                        graphAttributes.oppositeYAxisTitle ||
                        "",
                };
            }, [
                yAxisFilter,
                yAxisOppositeFilter,
                graphAttributes,
                widgetFilter,
            ]);

            const baseChartOptions = useMemo((): Highcharts.Options => {
                const createYAxis = (isOpposite = false) => ({
                    title: {
                        text: graphAttributes.hideYAxisTitle
                            ? ""
                            : isOpposite
                                ? yAxisTitles.oppositeYAxisTitle
                                : yAxisTitles.yAxisTitle,
                        align: "middle" as const,
                        style: {
                            color: COMMON_STYLES.secondaryColor,
                            fontFamily: COMMON_STYLES.fontFamily,
                        },
                    },
                    labels: {
                        overflow: "justify" as const,
                        style: {
                            color: COMMON_STYLES.secondaryColor,
                            fontFamily: COMMON_STYLES.fontFamily,
                        },
                        formatter: function (
                            this: Highcharts.AxisLabelsFormatterContextObject
                        ) {
                            return graphAttributes?.yAxisUnit
                                ? `${this.value}${graphAttributes?.yAxisUnit}`
                                : String(this.value);
                        },
                    },
                    tickAmount: yAxisTickAmount,
                    min: graphAttributes.min || 0,
                    max: maxYaxisValue,
                    ...(isOpposite && { opposite: true }),
                });

                // Configure y-axis based on whether we have opposite y-axis data
                // Need to check if we should show opposite axis using the same logic as oppositeYAxisSeries
                const shouldShowOppositeAxis = yAxisOppositeFilter &&
                    !(disableYaxisOppositeOnPillCount !== null && selectedPills?.length !== disableYaxisOppositeOnPillCount);

                const hasOppositeYAxis = shouldShowOppositeAxis && oppositeYAxisSeries.length > 0;

                const yAxis = hasOppositeYAxis || shouldShowOppositeAxis
                    ? [
                        createYAxis(false), // Primary y-axis
                        {
                            ...createYAxis(true), // Secondary y-axis
                            title: {
                                text: `${yAxisOppositeFilter}`,
                            }
                        }
                    ]
                    : graphAttributes.oppositeYAxisTitle
                        ? [createYAxis(false), createYAxis(true)]
                        : createYAxis(false);
                const xAxisConfig: any = {
                    categories,
                    title: {
                        text: graphAttributes?.hideXAxisTitle ? "" : "Dates",
                    },
                    lineWidth: 1,
                    labels: {
                        rotation: categories.length > minorTicksLabelLimit ? graphAttributes?.xAxis?.labels?.rotation || 0 : 0,
                        overflow: 'allow', // Allow labels to overflow without ellipses
                        style: {
                            color: COMMON_STYLES.secondaryColor,
                            fontFamily: COMMON_STYLES.fontFamily,
                            textOverflow: 'none', // Prevent ellipses
                        },
                        formatter: function (
                            this: Highcharts.AxisLabelsFormatterContextObject
                        ) {
                            return graphAttributes?.xAxisUnit
                                ? `${this.value}${graphAttributes?.xAxisUnit}`
                                : String(this.value);
                        },
                    },
                    tickmarkPlacement:
                        graphAttributes?.xAxis?.tickmarkPlacement || "on",
                    crosshair: graphAttributes?.xAxis?.crosshair || {},
                };

                // Center single category by adding padding
                if (categories.length === 1) {
                    xAxisConfig.min = -0.2;
                    xAxisConfig.max = 0.2;
                }
                const chartRightSpacing = isNestedChart ? 35 : 20;


                // diff = number of days between startDate and endDate
                let diff = categories.length - 1;

                if (diff >= minDatelabels && diff < 30) {
                    diff = 30;
                }

                // ticks = min(required DateLabels, diff + 1)
                const ticks = Math.min(minDatelabels, diff + 1);

                // gap = floor(diff / (ticks - 1))
                const gap = Math.floor(diff / (ticks - 1));

                // Generate tick positions with consistent gap spacing
                const labelPositions = new Set<number>();

                // Add ticks with equal distribution
                for (let i = 0; i < ticks; i++) {
                    labelPositions.add(i * gap);
                }

                // Use tickPositioner to force ticks at ALL positions
                xAxisConfig.tickPositioner = function () {
                    const positions = [];
                    for (let i = 0; i < categories.length; i++) {
                        positions.push(i);
                    }
                    return positions;
                };

                // Style the tick marks to be visible for all dates
                xAxisConfig.tickLength = 6;
                xAxisConfig.tickWidth = 1;
                xAxisConfig.tickColor = '#999999';

                xAxisConfig.labels = {
                    ...xAxisConfig.labels,
                    formatter: function (
                        this: Highcharts.AxisLabelsFormatterContextObject
                    ) {
                        // Only show label if this position is in our labelPositions set
                        if (!labelPositions.has(this.pos)) {
                            return ''; // Hide label for this position
                        }
                        return graphAttributes?.xAxisUnit
                            ? `${this.value}${graphAttributes?.xAxisUnit}`
                            : String(this.value);
                    },
                    style: {
                        fontSize: '10px',
                    }
                };

                return {
                    chart: {
                        type: chartType,
                        height: chartHeight || 220,
                        style: {
                            fontFamily: COMMON_STYLES.fontFamily,
                        } as unknown as Highcharts.CSSObject,
                        spacing: [10, chartRightSpacing, 10, 10], // Dynamic right spacing based on label count
                        // Performance optimizations for large datasets
                        animation: combinedSeries.length > 50 ? false : true,
                        reflow: true,
                    },
                    colors: graphColors,
                    title: { text: graphAttributes.title || "" },
                    xAxis: xAxisConfig,
                    yAxis,
                    tooltip: {
                        ...graphAttributes.tooltip,
                        valueDecimals: 2,
                        valueSuffix: graphAttributes?.yAxisUnit ? ` ${graphAttributes.yAxisUnit}` : "",
                        // Inject sorting data for tooltip formatter
                        ...(graphAttributes.tooltip?.formatter && pillFilterDropDownData && pillFilterDropDownData.length > 0 && {
                            formatter: function (this: Highcharts.TooltipFormatterContextObject): string {
                                const chart = (this.series as any).chart;
                                if (chart && !chart.userOptions.sortingConfig) {
                                    chart.userOptions.sortingConfig = {
                                        dropDownFiltersData: pillFilterDropDownData,
                                        primaryDimension: primaryDimension,
                                        sortByFiltersData: sortByFiltersData
                                    };
                                }
                                return graphAttributes.tooltip.formatter.call(this);
                            }
                        })
                    },
                    plotOptions: graphAttributes.plotOptions || {
                        spline: {
                            dataLabels: { enabled: false },
                            enableMouseTracking: true,
                            lineWidth: 2,
                            marker: {
                                enabled: false,
                                symbol: "circle",
                            },
                            // Performance optimizations
                            animation: combinedSeries.length > 50 ? false : true,
                            turboThreshold: 1000, // Increase threshold for large datasets
                        },
                        series: {
                            marker: {
                                enabled: false,
                                symbol: "circle",
                            },
                            // Performance optimizations
                            animation: combinedSeries.length > 50 ? false : true,
                            turboThreshold: 1000,
                            states: {
                                hover: {
                                    enabled: combinedSeries.length <= 100, // Disable hover for very large datasets
                                }
                            },
                            connectNulls: true,
                        },
                    },
                    legend: {
                        enabled: false,
                        labelFormatter: function () {
                            return capitalizeWords(this.name);
                        },
                    },
                    credits: { enabled: false },
                    ...(graphAttributes.formatter
                        ? {
                            tooltip: {
                                formatter: graphAttributes.formatter,
                                useHTML: true,
                            },
                        }
                        : {}),
                    series: combinedSeries,
                    lang: { noData: "No data to display" },
                    noData: {
                        style: {
                            fontWeight: "bold",
                            fontSize: "15px",
                            color: "#303030",
                        },
                    },
                    responsive: {
                        rules: [
                            {
                                condition: { maxWidth: 500 },
                                chartOptions: {
                                    legend: {
                                        align: "center",
                                        verticalAlign: "top",
                                        layout: "horizontal",
                                        y: 10,
                                    },
                                },
                            },
                        ],
                    },
                };
            }, [
                categories,
                combinedSeries,
                graphAttributes?.yAxisUnit,
                graphAttributes?.xAxisUnit,
                yAxisTickAmount,
                graphColors,
                chartType,
                yAxisTitles,
                chartHeight,
                selectedPills,
                yAxisOppositeFilter,
                pillFilterDropDownData,
                primaryDimension,
                sortByFiltersData
            ]);

            // Create final chart options with sync capabilities if enabled
            const chartOptions = useMemo((): Highcharts.Options => {
                if (enableTooltipSync && chartSyncId) {
                    return createSyncEnabledChartOptions(baseChartOptions, chartSyncId);
                }
                return baseChartOptions;
            }, [baseChartOptions, enableTooltipSync, chartSyncId]);

            const loadingSkeletonHeight = chartHeight;

            // Memoized chart key to prevent unnecessary re-renders
            const chartKey = useMemo(() => {
                const pillsKey = selectedPills?.map(p => p.value).sort().join('-') || 'no-pills';
                const filtersKey = `${yAxisFilter || 'no-y'}-${yAxisOppositeFilter || 'no-opp'}`;
                return `${identifier}-${pillsKey}-${filtersKey}-${combinedSeries.length}`;
            }, [identifier, selectedPills, yAxisFilter, yAxisOppositeFilter, combinedSeries.length]);

            // Show loading when either isLoading is true OR when pills are being initialized
            const shouldShowLoading = isLoading || (hasChartPillsFilters && (!selectedPills || selectedPills.length === 0))


            // Only show no data when we're not loading and actually have no data
            const showNoData = !shouldShowLoading && !originalSeries?.length;

            if (shouldShowLoading && !isChartLoadingRequired) {
                return (
                    <Skeleton.Node
                        className="w-100"
                        active
                        style={{ height: loadingSkeletonHeight }}
                    />
                );
            }

            if (showNoData && !isChartLoadingRequired) {
                return (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: loadingSkeletonHeight }}
                    >
                        <NoDataText />
                    </div>
                );
            }

            return (
                <div className="p-2">
                    {isWidgetFilterRequired && (
                        <div className="revstack-drill-down-chart-widget">
                            <div className="chart-actions-container">
                                <div>

                                    {buildWidgetCardFilters({
                                        filters: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                section: "",
                                                props: {
                                                    value: widgetFilter,
                                                    options: widgetFilterOptions,
                                                    width: 140,
                                                    onChange: (value: string) => setWidgetFilter(value),
                                                },
                                                disabled: widgetFilterConfig?.disabled || false
                                            }
                                        ]
                                    })}

                                </div>
                            </div>
                        </div>
                    )}
                    {isCustomLegendRequired && (
                        <CustomLegend
                            series={combinedSeries}
                            graphColors={graphColors}
                            formatForChannelId={formatForChannelId}
                        />
                    )}
                    <div>
                        {
                            isChartLoadingRequired && shouldShowLoading ?
                                <Skeleton.Node
                                    className="w-100"
                                    active
                                    style={{ height: loadingSkeletonHeight }}
                                />
                                :
                                <HighchartsReact
                                    key={chartKey}
                                    highcharts={Highcharts}
                                    options={chartOptions}
                                    ref={chartComponentRef}
                                    containerProps={{
                                        style: { width: "100%", height: chartHeight, },
                                    }}
                                />
                        }
                    </div>
                </div>
            );
        });

export default RevstackTrendsWidget;
